import React from "react";
import Box from "@mui/material/Box";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Path } from "../Path";
import { StatusToText } from "./StatusToText";
import { CoverSquare } from "./CoverImages";
import { FollowUpDuePill } from "./FollowUpDuePill";
import { getSeverity } from "./publisher-interest/follow-up-severity";
import CheckCircle from "@mui/icons-material/CheckCircle";
import CircularProgress from "@mui/material/CircularProgress";
import { CircularProgressSize } from "../theme";
import { enqueueSnackbar } from "notistack";
import {
  useAddConventionToGame,
  useGame,
  useRemoveConventionFromGame,
  useShortlistPublisher,
} from "@seabrookstudios/pitch2table";
import { useMinimumLoadingTime } from "../core/useMinimumLoadingTime";
import { DateTime } from "luxon";
import Typography from "@mui/material/Typography";

/**
 * @deprecated Use GameListEntry2 instead.
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").GameSummary} props.item
 */
export const GameListEntry = ({ item }) => {
  const params = useParams();
  const navigate = useNavigate();

  const selected = params.gameId === item.id;

  const onClickHandler = () => {
    navigate(Path.user().game(item.id));
  };

  return (
    <ListItemButton id={`game-${item.id}`} alignItems="flex-start" selected={selected} onClick={onClickHandler}>
      <ListItemAvatar>
        <CoverSquare game={item} width={48} height={48} />
      </ListItemAvatar>
      <ListItemText primary={item.name} secondary={item.tag} />
      <Stack direction="column" style={{ flexShrink: 0 }}>
        <ListItemIcon>{StatusToText[item.status]}</ListItemIcon>
        {item.nextAction && (
          <>
            <FollowUpDuePill severity={getSeverity(item.nextAction)} />
            <Box p={0.5} />
          </>
        )}
      </Stack>
    </ListItemButton>
  );
};

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").GameSummary} props.item
 * @param {boolean} props.noAlert
 * @param {import("react-router-dom").To|undefined} [props.link]
 */
export const GameListEntry2 = ({ item, noAlert = false, link }) => {
  return (
    <ListItemButton
      id={`game-${item.id}`}
      alignItems="flex-start"
      to={link ?? Path.game(item.id).link()}
      component={Link}
    >
      <ListItemAvatar>
        <CoverSquare game={item} width={48} height={48} />
      </ListItemAvatar>
      <ListItemText primary={item.name} secondary={item.tag} />
      <Stack direction="column" style={{ flexShrink: 0 }}>
        <ListItemIcon>{StatusToText[item.status]}</ListItemIcon>
        {item.nextAction && !noAlert && (
          <>
            <FollowUpDuePill severity={getSeverity(item.nextAction)} />
            <Box p={0.5} />
          </>
        )}
      </Stack>
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").ConventionId} props.conventionId
 * @param {import("@seabrookstudios/pitch2table-core").GameSummary} props.game
 * @param {boolean} props.isLinked
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId|undefined} props.linkedPublisherId
 */
export const EventFromRouteGameListItem = ({ conventionId, game, isLinked, linkedPublisherId }) => {
  const addGame = useAddConventionToGame(game.id);
  const removeGame = useRemoveConventionFromGame(game.id);
  const shortlist = useShortlistPublisher(game.id);

  const isPending = addGame.isPending || removeGame.isPending;
  const showLoading = useMinimumLoadingTime(isPending, 1000);

  const onAddGame = async () => {
    try {
      if (isLinked) {
        await removeGame.mutateAsync({ conventionId });
      } else {
        await addGame.mutateAsync({ conventionId });

        if (linkedPublisherId) {
          await shortlist.mutateAsync({ publisherIds: [linkedPublisherId] });
        }
      }
    } catch {
      enqueueSnackbar("Failed to toggle game", { variant: "error" });
    }
  };

  return (
    <ListItemButton key={game.id} onClick={onAddGame}>
      <ListItemAvatar>
        <CoverSquare game={game} width={48} height={48} />
      </ListItemAvatar>
      <ListItemText primary={game.name} secondary={game.tag} />
      {(isLinked || showLoading) && (
        <ListItemIcon>
          {showLoading ? <CircularProgress size={CircularProgressSize} /> : <CheckCircle color="success" />}
        </ListItemIcon>
      )}
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").GameSummary} props.linkedGame
 */
export const LinkedGameListItem = ({ linkedGame }) => {
  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate(Path.game(linkedGame.id).link());
  };

  return (
    <ListItemButton onClick={handleNavigate}>
      <ListItemAvatar>
        <CoverSquare game={linkedGame} width={48} height={48} />
      </ListItemAvatar>
      <ListItemText primary={linkedGame.name} secondary={linkedGame.tag} />
    </ListItemButton>
  );
};

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").GameSummary} props.item
 */
export const RecentGamesCardGameListEntry2 = ({ item }) => {
  const updatedTime = DateTime.fromMillis(item.updated || 0);
  const relativeTime = updatedTime.toRelative();

  return (
    <ListItemButton id={`game-${item.id}`} alignItems="flex-start" to={Path.game(item.id).link()} component={Link}>
      <ListItemAvatar>
        <CoverSquare game={item} width={48} height={48} />
      </ListItemAvatar>
      <ListItemText primary={item.name} secondary={`Updated ${relativeTime}`} />
      <Stack direction="column" style={{ flexShrink: 0 }}>
        <ListItemIcon>{StatusToText[item.status]}</ListItemIcon>
      </Stack>
    </ListItemButton>
  );
};

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const GameName = ({ gameId }) => {
  const { data: game } = useGame(gameId);
  return (
    <Typography component="span" variant="inherit">
      {game?.name}
    </Typography>
  );
};
